import bindAllMethods from '../../../../utils/bindAllMethods';
import WebNavigationHistory from '../WebNavigationHistory/WebNavigationHistory';
import * as T from '../INavigationHistory';
// import { JWebErrorHandler, ValueStoreFacade } from '../../../JWeb';
import type { v1Types } from '@clientos/storage-northbound-api';

export type VirtualNavigationHistoryStateType = {
  index: number;
  historyStack: VirtualNavigationHistoryStackItemType[];
};

export type VirtualNavigationHistoryStackItemType = {
  location: T.NavigationHistoryLocation;
  href: string;
};

const storageQuery = {
  name: 'VIRTUAL_NAVIGATION_HISTORY_STATE',
  source: 'CLIENTOS_COMMONS'
};

export default class VirtualNavigationHistory implements T.INavigationHistory {
  static async getCachedHistoryState(): Promise<
    VirtualNavigationHistoryStateType | undefined
  > {
    try {
      const storageNorthboundApi: v1Types.INorthboundAPIV1 =
        await System.import('@clientos/storage-northbound-api').then(
          (m) => m.default.v1
        );
      const response = await storageNorthboundApi.getObjectResource(
        storageQuery
      );

      if (response.error) {
        console.error(
          'VirtualNavigationHistory: Unable to retrieve virtual history state from Storage Northbound API. ',
          response.error
        );
        return undefined;
      }

      const result = response.value
        .resource as VirtualNavigationHistoryStateType;

      if (!result?.historyStack?.[result?.index]?.location?.pathname) {
        if (result) {
          console.warn(
            'VirtualNavigationHistory: Retrieved virtual history state is invalid. ',
            result
          );
        }
        return undefined;
      }

      return result;
    } catch {
      return undefined;
    }
  }

  private async _saveHistoryState(): Promise<void> {
    try {
      const storageNorthboundApi: v1Types.INorthboundAPIV1 =
        await System.import('@clientos/storage-northbound-api').then(
          (m) => m.default.v1
        );

      const resource: VirtualNavigationHistoryStateType = {
        index: this._index,
        historyStack: this._historyStack
      };

      const response = await storageNorthboundApi.store({
        metadata: {
          name: storageQuery.name,
          source: storageQuery.source,
          retentionPolicy: {
            type: 'ephemeral'
          }
        },
        resource
      } as any);

      if (response.error) {
        console.error(
          'VirtualNavigationHistory: Unable to save virtual history state to Storage Northbound API. ',
          response.error
        );
      }
    } catch (error) {
      console.error(
        'VirtualNavigationHistory: Unable to save virtual history state to Value Store',
        error
      );
    }
  }

  private _webNavigationHistory: WebNavigationHistory;
  private _index = -1;
  private _lastAction: T.NavigationHistoryAction;
  private _historyStack: VirtualNavigationHistoryStackItemType[] = [];
  private _listeners: Set<T.NavigationHistoryLocationListener> = new Set();

  get _history(): T.INavigationHistory['_history'] {
    return this._webNavigationHistory._history;
  }

  get length(): T.INavigationHistory['length'] {
    return this._historyStack.length;
  }
  get action(): T.INavigationHistory['action'] {
    return this._lastAction;
  }
  get location(): T.INavigationHistory['location'] {
    return this._historyStack?.[this._index]?.location;
  }

  constructor(
    options?: T.NavigationHistoryConstructorPropsType & {
      virtualHistoryState?: VirtualNavigationHistoryStateType;
    }
  ) {
    this._webNavigationHistory = new WebNavigationHistory(options);
    bindAllMethods(this);

    // Prepare initial state;
    const externalState = options?.virtualHistoryState;
    const externalCurrentLocation =
      externalState?.historyStack?.[externalState?.index]?.location;
    const browserCurrentLocation = this._webNavigationHistory.location;

    if (externalCurrentLocation) {
      try {
        this._historyStack = JSON.parse(
          JSON.stringify(externalState.historyStack)
        );
      } catch (error) {
        console.log(
          '@clientos-commons-northbound-api - VirtualNavigationHistory - constructor error:',
          error.message
        );
      }
      this._index = externalState.index;
      if (
        externalCurrentLocation?.pathname !== browserCurrentLocation?.pathname
      ) {
        this.push(browserCurrentLocation);
      } else {
        this.replace(externalCurrentLocation);
      }
    } else {
      this.push(browserCurrentLocation);
    }

    this._saveHistoryState().catch(console.error);
    // Consider creating a listener to web history changes to keep them in sync;
    // this._webNavigationHistory.listen((location) => {
    //   // this._saveHistoryState();
    //   console.log('!@#$ - Web history changed: ', {
    //     index: this._index,
    //     stack: JSON.parse(JSON.stringify(this._historyStack)),
    //     lastAction: this._lastAction,
    //     webLocation: location,
    //     virtualLocation: this.location,
    //     isSamePathname: location?.pathname === this.location?.pathname,
    //     isSameSearch: location?.search === this.location?.search,
    //     isSameHash: location?.hash === this.location?.hash,
    //     isSameState:
    //       JSON.stringify(location?.state || {}) ===
    //       JSON.stringify(this.location?.state || {})
    //   });
    // });
  }

  push(location: T.NavigationHistoryLocationDescriptor, state?: unknown): void {
    const newLocation = this._normalizeLocation(location, state);

    if (this._index < this._historyStack.length - 1) {
      this._historyStack = this._historyStack.slice(0, this._index + 1);
    }
    this._historyStack.push(this._createHistoryStackItem(newLocation));
    this._index += 1;
    this._lastAction = 'PUSH';
    this._webNavigationHistory.replace(newLocation);
    this._notifyListeners();
  }

  replace(
    location: T.NavigationHistoryLocationDescriptor,
    state?: unknown
  ): void {
    const newLocation = this._normalizeLocation(location, state);

    this._historyStack[this._index] = this._createHistoryStackItem(newLocation);
    this._lastAction = 'REPLACE';
    this._webNavigationHistory.replace(newLocation);
    this._notifyListeners();
  }

  go(n: number): void {
    if (n === 0) return;
    const newIndex = this._index + n;
    const historyStackItem = this._historyStack?.[newIndex];

    if (!historyStackItem) return;
    this._lastAction = newIndex < this._index ? 'POP' : 'PUSH';
    this._index = newIndex;
    this._webNavigationHistory.replace(historyStackItem.location);
    this._notifyListeners();
  }

  goBack(): void {
    this.go(-1);
  }

  goForward(): void {
    this.go(1);
  }

  block(
    prompt?: string | boolean | T.NavigationHistoryTransitionPromptHook
  ): T.NavigationHistoryUnregisterCallback {
    return this._webNavigationHistory?.block?.(prompt);
  }

  listen(
    listener: T.NavigationHistoryLocationListener
  ): T.NavigationHistoryUnregisterCallback {
    this._listeners.add(listener);
    return () => {
      this._listeners.delete(listener);
    };
  }

  createHref(location: T.NavigationHistoryLocationDescriptorObject): string {
    return this._webNavigationHistory.createHref(location);
  }

  // Required to change internal state before pushing to history stack otherwise the listeners won't get the correct location in some use cases;
  private _normalizeLocation(
    location: string | T.NavigationHistoryLocationDescriptor,
    state?: unknown
  ): T.NavigationHistoryLocation {
    const result: T.NavigationHistoryLocation = {
      pathname: '',
      search: '',
      hash: '',
      state: state
    };

    const unsanitizedPathname =
      typeof location === 'string' ? location : location.pathname || '';

    if (unsanitizedPathname) {
      const [pathname, searchAndHash] = unsanitizedPathname.split('?');
      result.pathname = pathname;
      if (searchAndHash) {
        const [search, hash] = searchAndHash.split('#');
        result.search = search ? `?${search}` : '';
        result.hash = hash ? `#${hash}` : '';
      }
    }

    if (typeof location !== 'string') {
      if (location.search) {
        if (!result?.search) {
          result.search = '?';
        } else {
          result.search += '&';
        }
        const newSearch = location.search.startsWith('?')
          ? location.search.slice(1)
          : location.search;
        result.search += newSearch;

        result.search = location.search;
      }
      if (location.hash) {
        result.hash = location.hash;
      }
      if (location.state !== undefined) {
        result.state = location.state;
      }
      if (location.key) {
        result.key = location.key;
      }
    }

    return result;
  }

  private _notifyListeners(): void {
    this._saveHistoryState();

    this._listeners.forEach((listener) => {
      listener(this.location, this.action);
    });
  }

  private _createHistoryStackItem(
    location: T.NavigationHistoryLocation
  ): VirtualNavigationHistoryStackItemType {
    return {
      location,
      href: window.location.origin + this.createHref(location)
    };
  }
}
